import type { PatchPortInfoPayload } from '~hooks/queries/port-info/use-patch-port-info'
import type { PortInfoFormData, PortInfoFile, AttachedFile } from '../types'
import { formatDate } from '~utils/dates'

function copyFieldToPayload(
  payload: PatchPortInfoPayload,
  key: string,
  value: unknown,
): void {
  const payloadKey = key as keyof PatchPortInfoPayload
  ;(payload as Record<string, unknown>)[payloadKey] = value
}

export function getFilesForField(
  files: PortInfoFile[] | undefined,
  fieldName: string,
): AttachedFile[] {
  if (!files) return []
  return files
    .filter((f) => f.field === fieldName)
    .map((f) => ({
      id: f._id,
      name: decodeURIComponent(f.title),
      fileSize: f.fileSize,
      signedUrl: f.signedUrl,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

const API_DATE_FORMAT = 'yyyy-MM-dd'

const NON_CLEARABLE_FIELDS = new Set([
  'vesselName',
  'imoNumber',
  'portName',
  'portCode',
  'portCountry',
  'terminalName',
  'berthName',
  'dateOfArrival',
  'dateOfDeparture',
])

const DATE_FIELDS = new Set([
  'dateOfArrival',
  'dateOfDeparture',
  'dateOfInformation',
])
const SKIP_FIELDS = new Set([
  'port',
  'continent',
  'country',
  'history',
  'updatedBy',
  'updatedAt',
  'files',
])

export function formatDateForPayload(date: Date | undefined): string {
  if (!date) return ''
  return formatDate(date, API_DATE_FORMAT)
}

export function hasUnsavedChanges(formData: PortInfoFormData): boolean {
  return Object.values(formData).some((value) => value !== undefined)
}

export function buildPatchPayload(
  formData: PortInfoFormData,
): PatchPortInfoPayload {
  const payload: PatchPortInfoPayload = {}

  for (const [key, value] of Object.entries(formData)) {
    if (SKIP_FIELDS.has(key) || value === undefined) continue

    if (DATE_FIELDS.has(key)) {
      const dateKey = key as 'dateOfArrival' | 'dateOfDeparture'
      payload[dateKey] = formatDateForPayload(value as Date)
    } else {
      copyFieldToPayload(payload, key, value)
    }
  }

  if (formData.port?.port) {
    payload.portName = formData.port.port.PortName
    payload.portCountry = formData.port.port.CountryName
    payload.portCountryCode = formData.port.port.CountryCode
    payload.portCode = formData.port.port.PortCode
    payload.portContinentName = formData.port.port.ContinentName
    payload.portContinentCode = formData.port.port.ContinentCode
  }

  if (formData.continent?.continent) {
    payload.continentName = formData.continent.continent.ContinentName
    payload.continentCode = formData.continent.continent.ContinentCode
  }

  if (formData.country?.country) {
    payload.countryName = formData.country.country.CountryName
    payload.countryCode = formData.country.country.CountryCode
    payload.continentName = formData.country.country.ContinentName
    payload.continentCode = formData.country.country.ContinentCode
  }

  return payload
}

export function buildEditPatchPayload(
  formData: PortInfoFormData,
  initialData: PortInfoFormData,
): PatchPortInfoPayload {
  const payload: PatchPortInfoPayload = {}

  for (const [key, value] of Object.entries(formData)) {
    if (SKIP_FIELDS.has(key) || value === undefined) continue

    const initial = initialData[key as keyof PortInfoFormData]
    const isDate = DATE_FIELDS.has(key)
    const formatted = isDate ? formatDateForPayload(value as Date) : value
    const initialFormatted = isDate
      ? formatDateForPayload(initial as Date)
      : initial

    if (formatted !== initialFormatted) {
      copyFieldToPayload(payload, key, formatted)
    }
  }

  const currentPort = formData.port?.port
  const initialPort = initialData.port?.port
  if (currentPort?.PortCode !== initialPort?.PortCode) {
    payload.portName = currentPort?.PortName
    payload.portCountry = currentPort?.CountryName
    payload.portCountryCode = currentPort?.CountryCode
    payload.portCode = currentPort?.PortCode
    payload.portContinentName = currentPort?.ContinentName
    payload.portContinentCode = currentPort?.ContinentCode
  }

  const currentContinent = formData.continent?.continent
  const initialContinent = initialData.continent?.continent
  if (currentContinent?.ContinentCode !== initialContinent?.ContinentCode) {
    payload.continentName = currentContinent?.ContinentName
    payload.continentCode = currentContinent?.ContinentCode
  }

  const currentCountry = formData.country?.country
  const initialCountry = initialData.country?.country
  if (currentCountry?.CountryCode !== initialCountry?.CountryCode) {
    payload.countryName = currentCountry?.CountryName
    payload.countryCode = currentCountry?.CountryCode
    payload.continentName = currentCountry?.ContinentName
    payload.continentCode = currentCountry?.ContinentCode
  }

  for (const [key, initial] of Object.entries(initialData)) {
    if (NON_CLEARABLE_FIELDS.has(key)) continue
    if (initial != null && formData[key as keyof PortInfoFormData] == null) {
      copyFieldToPayload(payload, key, null)
    }
  }

  return payload
}

const PAGE_ONE_REQUIRED_FIELDS: (keyof PortInfoFormData)[] = [
  'type',
  'vesselName',
  'imoNumber',
]

const PAGE_TWO_REQUIRED_FIELDS: (keyof PortInfoFormData)[] = [
  'port',
  'terminalName',
  'berthName',
  'dateOfArrival',
  'dateOfDeparture',
  'operation',
  'cargoType',
  'arrivalDraft',
  'departureDraft',
  'ispsSecurityLevel',
  'cargoConnections',
  'manifoldPressure',
]

const PAGE_TWO_CONTINENT_REQUIRED_FIELDS: (keyof PortInfoFormData)[] = [
  'continent',
  'dateOfInformation',
  'subject',
]

const PAGE_TWO_COUNTRY_REQUIRED_FIELDS: (keyof PortInfoFormData)[] = [
  'country',
  'dateOfInformation',
  'subject',
]

function isMissingValue(value: unknown): boolean {
  const isEmptyPortSelection =
    typeof value === 'object' &&
    value !== null &&
    'port' in value &&
    !(value as { port?: unknown }).port

  const isEmptyContinentSelection =
    typeof value === 'object' &&
    value !== null &&
    'continent' in value &&
    !(value as { continent?: unknown }).continent

  const isEmptyCountrySelection =
    typeof value === 'object' &&
    value !== null &&
    'country' in value &&
    !(value as { country?: unknown }).country

  return (
    value === undefined ||
    value === null ||
    value === '' ||
    isEmptyPortSelection ||
    isEmptyContinentSelection ||
    isEmptyCountrySelection
  )
}

const REQUIRED_FIELDS_BY_PAGE: Record<
  '1' | '2' | '2-simple' | 'all',
  (keyof PortInfoFormData)[]
> = {
  '1': PAGE_ONE_REQUIRED_FIELDS,
  '2': PAGE_TWO_REQUIRED_FIELDS,
  '2-simple': [],
  all: [...PAGE_ONE_REQUIRED_FIELDS, ...PAGE_TWO_REQUIRED_FIELDS],
}

export function getMissingRequiredFields(
  formData: PortInfoFormData,
  page: '1' | '2' | '2-simple' | 'all' = 'all',
): (keyof PortInfoFormData)[] {
  let requiredFields: (keyof PortInfoFormData)[]

  if (page === '2-simple') {
    if (formData.type === 'continent') {
      requiredFields = PAGE_TWO_CONTINENT_REQUIRED_FIELDS
    } else if (formData.type === 'country') {
      requiredFields = PAGE_TWO_COUNTRY_REQUIRED_FIELDS
    } else {
      requiredFields = []
    }
  } else {
    requiredFields = REQUIRED_FIELDS_BY_PAGE[page]
  }

  return requiredFields.filter((field) => isMissingValue(formData[field]))
}

export function portInfoFileTitleEncode(title: string): string {
  const acceptedChars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-.,& '
  return title
    .split('')
    .map((char) => {
      if (acceptedChars.includes(char)) return char
      // Use encodeURIComponent for proper UTF-8 encoding
      const encoded = encodeURIComponent(char)
      // encodeURIComponent doesn't encode ()!~*' - manually encode if unchanged
      if (encoded === char) {
        const bytes = new TextEncoder().encode(char)
        return Array.from(bytes)
          .map((byte) => `%${byte.toString(16).toUpperCase().padStart(2, '0')}`)
          .join('')
      }
      return encoded
    })
    .join('')
}
